import type { FC } from "react";
import { cn } from "./utils/cn";

export type ToggleSize = "sm" | "md";

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: ToggleSize;
  "aria-label"?: string;
  ["data-cy"]?: string;
}

const trackSize: Record<ToggleSize, string> = {
  sm: "h-6 w-12",
  md: "h-6 w-12 md:h-7 md:w-14"
};

const knobSize: Record<ToggleSize, string> = {
  sm: "h-5 w-5",
  md: "h-5 w-5 md:h-6 md:w-6"
};

const knobTravel: Record<ToggleSize, string> = {
  sm: "translate-x-[20px]",
  md: "translate-x-[20px] md:translate-x-[24px]"
};

/**
 * Canonical on/off switch. Supersedes common/Toggle. Wrapped in dir="ltr" so
 * the knob always travels left→right regardless of page direction.
 */
const Toggle: FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  size = "md",
  "aria-label": ariaLabel,
  ...props
}) => {
  return (
    <div className="flex items-center justify-center" dir="ltr">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        data-cy={props["data-cy"]}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          "transition-colors flex cursor-pointer items-center rounded-pill border border-transparent p-1 duration-200 focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-0",
          trackSize[size],
          disabled ?
          "bg-gray-200" :
          checked ?
          "bg-secondary" :
          "bg-menu/15 dark:bg-white/20",
          disabled && "cursor-not-allowed"
        )}>

        <span
          className={cn(
            "transform rounded-pill shadow-e1 transition-transform duration-200 ease-out-soft",
            knobSize[size],
            // `switch-knob-on` keeps the knob white in BOTH themes — global.css
            // force-darkens every white bg utility in dark mode, so the canonical
            // switch handle uses a dedicated marker class instead.
            disabled ? "bg-gray-400" : "switch-knob-on",
            checked && knobTravel[size]
          )} />

      </button>
    </div>);

};

export default Toggle;